/**
 * VariantFormModal — Story 11.4b.
 *
 * Modal de Add/Edit pra uma variante de template em uma campanha. Recebe
 * `variant?: CampaignTemplateDto` via props pra alternar entre criação
 * (`undefined`) e edição (DTO existente).
 *
 * Validação client-side espelha o backend (defense in depth — backend tem
 * TypeBox + runtime validator + CHECK constraint):
 *   - variant_letter: `^[A-Z]$` (single uppercase)
 *   - position: integer >= 0
 *   - variable_mappings keys: `^[1-9]\d?$` (1..99); values ∈ VARIABLE_REGISTRY
 *
 * Sucesso: toast verde + close + invalida query da lista.
 * Erro: toast vermelho com message da API + modal continua aberto.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, apiClient } from '@/lib/api';
import {
  VariableMappingsEditor,
  type VariableMappings,
} from '@/components/campaigns/VariableMappingsEditor';
import { VariantMappingPreview } from '@/components/campaigns/VariantMappingPreview';

export interface CampaignTemplateDto {
  id: string;
  campaign_id: string;
  meta_template_id: string;
  variant_letter: string;
  variable_mappings: VariableMappings;
  weight: number;
  active: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

interface MetaTemplateLite {
  id: string;
  metaTemplateName: string;
  status: string;
  active: boolean;
  /** Story 11.5 — usado pelo VariantMappingPreview pra render live com sample values. */
  contentPreview?: string | null;
}

interface TemplatesListResponse {
  data: MetaTemplateLite[];
}

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  campaignId: string;
  /** Quando `undefined`, modal opera em modo Add. */
  variant?: CampaignTemplateDto;
}

interface FormState {
  variantLetter: string;
  metaTemplateId: string;
  variableMappings: VariableMappings;
  position: number;
  active: boolean;
}

const DEFAULT_FORM: FormState = {
  variantLetter: '',
  metaTemplateId: '',
  variableMappings: {},
  position: 0,
  active: true,
};

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as {
      error?: { code?: string; message?: string; field?: string };
    };
    if (body?.error?.message) {
      const field = body.error.field ? ` (campo: ${body.error.field})` : '';
      return `${body.error.message}${field}`;
    }
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function VariantFormModal({ open, onOpenChange, campaignId, variant }: Props) {
  const queryClient = useQueryClient();
  const isEdit = !!variant;
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  useEffect(() => {
    if (!open) return;
    if (variant) {
      setForm({
        variantLetter: variant.variant_letter,
        metaTemplateId: variant.meta_template_id,
        variableMappings: variant.variable_mappings ?? {},
        position: variant.position,
        active: variant.active,
      });
    } else {
      setForm(DEFAULT_FORM);
    }
    setErrors({});
  }, [open, variant]);

  const templatesQuery = useQuery({
    queryKey: ['admin', 'meta-templates', 'approved-active'],
    queryFn: () =>
      // Backend valida `pageSize <= 100` (templates.schemas.ts). MVP tem <30
      // templates approved, então 100 cobre. Quando ultrapassar, paginar.
      apiClient.get<TemplatesListResponse>(
        '/admin/templates?status=approved&pageSize=100&active=true',
      ),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        meta_template_id: form.metaTemplateId,
        variable_mappings: form.variableMappings,
        position: form.position,
        active: form.active,
      };
      if (!isEdit) {
        body.variant_letter = form.variantLetter;
      }
      if (isEdit) {
        return apiClient.put<CampaignTemplateDto>(
          `/admin/campaigns/${campaignId}/templates/${variant!.id}`,
          body,
        );
      }
      return apiClient.post<CampaignTemplateDto>(`/admin/campaigns/${campaignId}/templates`, body);
    },
    onSuccess: (data) => {
      toast.success(`Variante ${data.variant_letter} salva.`);
      onOpenChange(false);
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'campaigns', campaignId, 'templates'],
      });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao salvar variante.'));
    },
  });

  function validate(): boolean {
    const next: typeof errors = {};
    if (!isEdit) {
      if (!/^[A-Z]$/.test(form.variantLetter)) {
        next.variantLetter = 'Use uma única letra maiúscula (A-Z).';
      }
    }
    if (!form.metaTemplateId) {
      next.metaTemplateId = 'Selecione um template Meta.';
    }
    if (!Number.isInteger(form.position) || form.position < 0) {
      next.position = 'Posição deve ser inteiro >= 0.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] flex-col gap-4 overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar variante' : 'Adicionar variante'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Variante ${variant!.variant_letter} — campos vazios mantêm o valor atual.`
              : 'Defina a letra (A/B/C/...), o template Meta e o mapping posicional opcional.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isEdit && (
            <div className="space-y-1">
              <Label htmlFor="variant-letter">Letra da variante</Label>
              <Input
                id="variant-letter"
                value={form.variantLetter}
                onChange={(e) =>
                  setForm((f) => ({ ...f, variantLetter: e.target.value.toUpperCase() }))
                }
                placeholder="A"
                maxLength={1}
                aria-invalid={!!errors.variantLetter}
                aria-describedby={errors.variantLetter ? 'variant-letter-error' : undefined}
              />
              {errors.variantLetter && (
                <p id="variant-letter-error" className="text-xs text-destructive">
                  {errors.variantLetter}
                </p>
              )}
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="meta-template">Template Meta</Label>
            <select
              id="meta-template"
              value={form.metaTemplateId}
              onChange={(e) => setForm((f) => ({ ...f, metaTemplateId: e.target.value }))}
              className="h-9 w-full rounded border bg-background px-2 text-sm"
              aria-invalid={!!errors.metaTemplateId}
              aria-describedby={errors.metaTemplateId ? 'meta-template-error' : undefined}
            >
              <option value="">— selecione —</option>
              {templatesQuery.data?.data
                .filter((t) => t.active && t.status === 'approved')
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.metaTemplateName}
                  </option>
                ))}
            </select>
            {errors.metaTemplateId && (
              <p id="meta-template-error" className="text-xs text-destructive">
                {errors.metaTemplateId}
              </p>
            )}
          </div>

          <VariableMappingsEditor
            value={form.variableMappings}
            onChange={(next) => setForm((f) => ({ ...f, variableMappings: next }))}
            disabled={mutation.isPending}
            id="variant-mappings"
          />

          {(() => {
            const selectedTemplate = templatesQuery.data?.data.find(
              (t) => t.id === form.metaTemplateId,
            );
            return (
              <VariantMappingPreview
                contentPreview={selectedTemplate?.contentPreview ?? null}
                variableMappings={form.variableMappings}
                templateName={selectedTemplate?.metaTemplateName}
              />
            );
          })()}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="variant-position">Posição</Label>
              <Input
                id="variant-position"
                type="number"
                min={0}
                value={form.position}
                onChange={(e) => setForm((f) => ({ ...f, position: Number(e.target.value) }))}
                aria-invalid={!!errors.position}
              />
              {errors.position && <p className="text-xs text-destructive">{errors.position}</p>}
            </div>

            <div className="flex items-end gap-2">
              <input
                id="variant-active"
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
              <Label htmlFor="variant-active">Ativa</Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Salvando…' : isEdit ? 'Salvar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
